/**
 * dataService.ts — AVH Portail RH
 *
 * Migration SharePoint → VPS API (FastAPI sur https://avhrh.avh.asso.fr)
 *
 * Les fonctions qui lisent/écrivent des données métier RH appellent désormais le VPS
 * via vpsClient et prennent un `apiToken` (audience = App ID URI).
 *
 * Les fonctions qui accèdent à Entra/Exchange via Graph (extensionAttributes, manager,
 * sendMail, upload SharePoint) restent sur graphClient et prennent un `graphToken`.
 */
import { graphGet, graphPost, graphPatch } from "./graphClient";
import { vpsGet, vpsPost, vpsPatch, vpsPut, vpsDelete } from "./vpsClient";
import { SHAREPOINT_SITE_ID } from "./sharePointConfig";
function str(v, fallback = "") {
    return v || fallback;
}
function num(v, fallback = 0) {
    return typeof v === "number" ? v : fallback;
}
function bool(v, fallback = false) {
    return typeof v === "boolean" ? v : fallback;
}
// Humanise les statuts snake_case de la DB vers labels affichables
const LEAVE_STATUS_LABELS = {
    en_attente: "En attente",
    en_attente_n1: "En attente N+1",
    en_attente_n2: "En attente N+2",
    en_attente_rh: "En attente RH",
    approuve: "Approuvé",
    refuse: "Refusé",
    annule: "Annulé",
    traite: "Traité",
    erreur: "Erreur",
};
function humanizeLeaveStatus(s) {
    return LEAVE_STATUS_LABELS[s] || s;
}
function capitalizeBadgeStatut(s) {
    if (!s)
        return "Brouillon";
    return s.charAt(0).toUpperCase() + s.slice(1);
}
// ============ LEAVE REQUESTS → /api/conges ============
function mapLeaveRequest(raw) {
    return {
        id: str(raw.id),
        employeeEmail: str(raw.employee_email),
        employeeName: str(raw.employee_name),
        leaveType: str(raw.leave_type),
        startDate: str(raw.start_date),
        endDate: str(raw.end_date),
        entryMode: (str(raw.entry_mode) || "full"),
        startPeriod: raw.start_period,
        endPeriod: raw.end_period,
        daysCount: num(raw.days_count),
        status: humanizeLeaveStatus(str(raw.status) || "en_attente"),
        comment: str(raw.comment),
        managerEmail: str(raw.manager_email),
        createdAt: str(raw.created_at) || undefined,
    };
}
export async function getAllLeaveRequests(apiToken) {
    const data = await vpsGet("/api/conges", apiToken);
    const mapped = data.map(mapLeaveRequest);
    // Déduplication par id (sécurité contre doublons backend)
    const seen = new Set();
    return mapped.filter((r) => { if (seen.has(r.id))
        return false; seen.add(r.id); return true; });
}
export async function createLeaveRequest(apiToken, fields) {
    const body = {
        employee_email: fields.EmployeeEmail,
        employee_name: fields.EmployeeName,
        leave_type: fields.LeaveType,
        start_date: fields.StartDate,
        end_date: fields.EndDate,
        entry_mode: fields.EntryMode,
        start_period: fields.StartPeriod,
        end_period: fields.EndPeriod,
        days_count: fields.DaysCount,
        status: fields.Status,
        comment: fields.Comment,
        manager_email: fields.ManagerEmail,
    };
    const res = await vpsPost("/api/conges", body, apiToken);
    return str(res.id);
}
export async function updateLeaveRequestStatus(apiToken, itemId, status) {
    await vpsPatch(`/api/conges/${itemId}/status`, { status }, apiToken);
}
export async function deleteLeaveRequest(apiToken, itemId) {
    await vpsDelete(`/api/conges/${itemId}`, apiToken);
}
// ============ SIGNALEMENTS → /api/signalements ============
function mapSignalement(raw) {
    return {
        id: str(raw.id),
        signaleurEmail: str(raw.signaleur_email),
        signaleurName: str(raw.signaleur_name),
        categorie: str(raw.categorie),
        description: str(raw.description),
        urgence: str(raw.urgence) || "Normal",
        statut: str(raw.statut) || "Nouveau",
        responsableActuel: str(raw.responsable_actuel),
        responsableAttendu: str(raw.responsable_attendu),
        reponseRH: str(raw.reponse_rh),
        createdAt: str(raw.created_at) || undefined,
    };
}
export async function getAllSignalements(apiToken) {
    const data = await vpsGet("/api/signalements", apiToken);
    return data.map(mapSignalement);
}
export async function createSignalement(apiToken, fields) {
    const res = await vpsPost("/api/signalements", fields, apiToken);
    return str(res.id);
}
export async function updateSignalement(apiToken, itemId, fields) {
    await vpsPatch(`/api/signalements/${itemId}`, fields, apiToken);
}
// ============ APPROBATEURS → /api/approbateurs ============
function mapApprobateur(raw) {
    // L'API retourne email/display_name (alias des JOINs), pas employee_email/employee_name
    return {
        id: str(raw.id),
        employeeEmail: str(raw.employee_email || raw.email),
        employeeName: str(raw.employee_name || raw.display_name),
        lieu: str(raw.lieu || raw.extension_attribute2),
        service: str(raw.service),
        role: str(raw.role) || "N+1",
        disponible: bool(raw.disponible, true),
        source: str(raw.source) || "Manuel",
        approbateur2Email: str(raw.approbateur2_email || raw.backup_email),
    };
}
export async function getAllApprobateurs(apiToken) {
    const data = await vpsGet("/api/approbateurs", apiToken);
    // L'API retourne une ligne par approbateur (employee_id = N+1, backup = N+2 implicite)
    // On explose en 2 lignes si backup existe, pour la vue Approbateurs qui attend N+1/N+2 séparés
    const result = [];
    for (const raw of data) {
        result.push(mapApprobateur(raw));
        if (raw.backup_email || raw.backup_approver_id) {
            result.push({
                id: str(raw.id) + "_n2",
                employeeEmail: str(raw.backup_email),
                employeeName: str(raw.backup_name),
                lieu: str(raw.lieu || raw.extension_attribute2),
                service: str(raw.service),
                role: "N+2",
                disponible: bool(raw.disponible, true),
                source: str(raw.source) || "Manuel",
                approbateur2Email: "",
            });
        }
    }
    return result;
}
export async function createApprobateur(apiToken, fields) {
    const res = await vpsPost("/api/approbateurs", fields, apiToken);
    return str(res.id);
}
export async function updateApprobateur(apiToken, itemId, fields) {
    await vpsPatch(`/api/approbateurs/${itemId}`, fields, apiToken);
}
export async function deleteApprobateur(apiToken, itemId) {
    await vpsDelete(`/api/approbateurs/${itemId}`, apiToken);
}
// ============ EXCEPTIONS → /api/exceptions ============
function mapException(raw) {
    return {
        id: str(raw.id),
        salarie: str(raw.salarie),
        approbateur1: str(raw.approbateur1),
        approbateur2: str(raw.approbateur2),
        motif: str(raw.motif),
        actif: bool(raw.actif, true),
    };
}
export async function getAllExceptions(apiToken) {
    const data = await vpsGet("/api/exceptions", apiToken);
    return data.map(mapException);
}
export async function createException(apiToken, fields) {
    const res = await vpsPost("/api/exceptions", fields, apiToken);
    return str(res.id);
}
export async function updateException(apiToken, itemId, fields) {
    await vpsPatch(`/api/exceptions/${itemId}`, fields, apiToken);
}
export async function deleteException(apiToken, itemId) {
    await vpsDelete(`/api/exceptions/${itemId}`, apiToken);
}
// ============ JOURS FERIES → /api/jours-feries ============
function mapJourFerie(raw) {
    return {
        id: str(raw.id),
        dateFerie: str(raw.date_ferie).substring(0, 10),
        libelle: str(raw.libelle),
        annee: num(raw.annee),
        recurrent: bool(raw.recurrent),
    };
}
export async function getAllJoursFeries(apiToken) {
    const data = await vpsGet("/api/jours-feries", apiToken);
    return data.map(mapJourFerie);
}
export async function createJourFerie(apiToken, fields) {
    const res = await vpsPost("/api/jours-feries", fields, apiToken);
    return str(res.id);
}
export async function updateJourFerie(apiToken, itemId, fields) {
    await vpsPut(`/api/jours-feries/${itemId}`, fields, apiToken);
}
export async function deleteJourFerie(apiToken, itemId) {
    await vpsDelete(`/api/jours-feries/${itemId}`, apiToken);
}
// ============ CONFIGURATION → /api/config ============
function mapConfigItem(raw) {
    return {
        id: str(raw.id),
        cle: str(raw.cle),
        valeur: str(raw.valeur),
        typeCode: str(raw.type_code) || undefined,
        libelle: str(raw.libelle) || undefined,
        delaiMin: raw.delai_min,
        demiJournee: str(raw.demi_journee) || undefined,
        estTypeAbsence: raw.est_type_absence,
    };
}
export async function getAllConfig(apiToken) {
    const data = await vpsGet("/api/config", apiToken);
    return data.map(mapConfigItem);
}
export async function createConfigItem(apiToken, fields) {
    const res = await vpsPost("/api/config", fields, apiToken);
    return str(res.id);
}
export async function updateConfigItem(apiToken, itemId, fields) {
    // La clé est passée dans les fields pour construire l'URL REST
    const cle = str(fields.cle || fields.Cle || itemId);
    await vpsPut(`/api/config/${cle}`, fields, apiToken);
}
export async function deleteConfigItem(apiToken, itemId) {
    await vpsDelete(`/api/config/${itemId}`, apiToken);
}
// ============ DEMANDES DE CHANGEMENT → /api/changements ============
/**
 * Génère un code unique permanent de format : CHG-YYYYMMDD-HHMMSS-XXXXX
 * Le suffixe est un hash court dérivé de crypto pour garantir l'unicité.
 */
export function generateChangeRequestCode() {
    const now = new Date();
    const date = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, "0"),
        String(now.getDate()).padStart(2, "0"),
    ].join("");
    const time = [
        String(now.getHours()).padStart(2, "0"),
        String(now.getMinutes()).padStart(2, "0"),
        String(now.getSeconds()).padStart(2, "0"),
    ].join("");
    const rand = Array.from(crypto.getRandomValues(new Uint8Array(4)))
        .map((b) => b.toString(36).padStart(2, "0"))
        .join("")
        .substring(0, 5)
        .toUpperCase();
    return `CHG-${date}-${time}-${rand}`;
}
function mapDemandeChangement(raw) {
    return {
        id: str(raw.id),
        codeUnique: str(raw.code_unique),
        employeeId: str(raw.employee_id),
        employeeEmail: str(raw.employee_email),
        employeeName: str(raw.employee_name),
        typeChangement: (str(raw.type_changement) || "Autre"),
        champCible: str(raw.champ_cible),
        valeurActuelle: str(raw.valeur_actuelle),
        valeurDemandee: str(raw.valeur_demandee),
        motif: str(raw.motif),
        statut: (str(raw.statut) || "En attente"),
        demandeurEmail: str(raw.demandeur_email),
        demandeurName: str(raw.demandeur_name),
        messageErreur: str(raw.message_erreur),
        dateTraitement: str(raw.date_traitement),
        createdAt: str(raw.created_at) || undefined,
    };
}
export async function getAllDemandesChangement(apiToken) {
    const data = await vpsGet("/api/changements", apiToken);
    return data.map(mapDemandeChangement);
}
export async function createDemandeChangement(apiToken, fields) {
    const res = await vpsPost("/api/changements", fields, apiToken);
    return str(res.id);
}
export async function updateDemandeChangement(apiToken, itemId, fields) {
    // Si les fields contiennent un statut d'annulation, utiliser /cancel
    if (fields.statut === "Annule" || fields.Statut === "Annule") {
        await vpsPatch(`/api/changements/${itemId}/cancel`, fields, apiToken);
    }
    else {
        await vpsPatch(`/api/changements/${itemId}/status`, fields, apiToken);
    }
}
// ============ DOSSIER SALARIE → /api/dossier ============
function mapDossierRecord(raw) {
    return {
        id: str(raw.id),
        employeeEmail: str(raw.employee_email),
        employeeName: str(raw.employee_name),
        recordType: (str(raw.record_type) || "Note"),
        dateDebut: str(raw.date_debut),
        dateFin: str(raw.date_fin),
        libelle: str(raw.libelle),
        description: str(raw.description),
        categorie: str(raw.categorie),
        resultat: str(raw.resultat),
        organisme: str(raw.organisme),
        dureeHeures: raw.duree_heures ?? null,
        auteurEmail: str(raw.auteur_email),
        auteurName: str(raw.auteur_name),
        confidentiel: bool(raw.confidentiel),
        pieceJointeUrl: str(raw.piece_jointe_url),
        createdAt: str(raw.created_at) || undefined,
    };
}
export async function getDossierByEmployee(apiToken, employeeId) {
    const data = await vpsGet(`/api/dossier/${employeeId}`, apiToken);
    return data.map(mapDossierRecord);
}
export async function createDossierRecord(apiToken, fields) {
    const res = await vpsPost("/api/dossier", fields, apiToken);
    return str(res.id);
}
export async function updateDossierRecord(apiToken, itemId, fields) {
    await vpsPatch(`/api/dossier/${itemId}`, fields, apiToken);
}
export async function deleteDossierRecord(apiToken, itemId) {
    await vpsDelete(`/api/dossier/${itemId}`, apiToken);
}
// ============ ROLES → /api/roles ============
function mapRoleRecord(raw) {
    // API JOIN retourne e.email et e.name (pas user_email/user_name)
    return {
        id: str(raw.id),
        userEmail: str(raw.email || raw.user_email),
        userName: str(raw.display_name || raw.name || raw.user_name),
        role: (str(raw.role) || "Salarie"),
        actif: bool(raw.actif, true),
    };
}
export async function getAllRoles(apiToken) {
    const data = await vpsGet("/api/roles", apiToken);
    return data.map(mapRoleRecord);
}
export async function getUserRole(apiToken, _email) {
    try {
        const res = await vpsGet("/api/me", apiToken);
        return (str(res.role) || "Salarie");
    }
    catch {
        return "Salarie";
    }
}
// Backend attend { employee_email, role } — résout l'employé par email/UPN
export async function createRole(apiToken, fields) {
    const res = await vpsPost("/api/roles", { employee_email: fields.UserEmail, role: fields.Role }, apiToken);
    return str(res.id);
}
export async function updateRole(apiToken, _itemId, fields) {
    const email = (fields.employee_email || fields.UserEmail || fields.user_email);
    const role = (fields.Role || fields.role);
    const res = await vpsPost("/api/roles", { employee_email: email, role }, apiToken);
    return str(res.id);
}
// Suppression par UUID de la ligne roles_utilisateurs (record.id)
export async function deleteRole(apiToken, roleId) {
    await vpsDelete(`/api/roles/${roleId}`, apiToken);
}
// ============ BADGEAGES → /api/badgeages ============
function mapBadgeage(raw) {
    // API retourne: email, display_name (JOIN employees), semaine_debut (pas semaine)
    // statut en minuscules: brouillon/soumis/valide/rejete → capitaliser
    return {
        id: str(raw.id),
        employeeEmail: str(raw.employee_email || raw.email),
        employeeName: str(raw.employee_name || raw.display_name),
        semaine: str(raw.semaine || raw.semaine_debut),
        dateJour: str(raw.date_jour),
        entree1: str(raw.entree1),
        sortie1: str(raw.sortie1),
        entree2: str(raw.entree2),
        sortie2: str(raw.sortie2),
        commentaire: str(raw.commentaire),
        statut: capitalizeBadgeStatut(str(raw.statut)),
        valideurEmail: str(raw.valideur_email),
        valideurName: str(raw.valideur_name),
        dateValidation: str(raw.date_validation),
        source: (str(raw.source) || ""),
        createdAt: str(raw.created_at) || undefined,
    };
}
export async function getBadgeagesByEmployee(apiToken, employeeId, semaine, dateJour) {
    const params = new URLSearchParams();
    if (employeeId)
        params.set("employee_id", employeeId);
    if (semaine)
        params.set("semaine_debut", semaine);
    if (dateJour)
        params.set("date_jour", dateJour);
    const qs = params.toString();
    const data = await vpsGet(`/api/badgeages${qs ? "?" + qs : ""}`, apiToken);
    return data.map(mapBadgeage);
}
export async function getBadgeagesByStatut(apiToken, statut) {
    const data = await vpsGet(`/api/badgeages?statut=${encodeURIComponent(statut)}`, apiToken);
    return data.map(mapBadgeage);
}
export async function createBadgeage(apiToken, fields) {
    const res = await vpsPost("/api/badgeages", fields, apiToken);
    return str(res.id);
}
export async function updateBadgeage(apiToken, itemId, fields) {
    await vpsPatch(`/api/badgeages/${itemId}`, fields, apiToken);
}
// Pas de DELETE badgeage — annulation via PATCH
export async function deleteBadgeage(apiToken, itemId) {
    await vpsPatch(`/api/badgeages/${itemId}`, { statut: "Annule" }, apiToken);
}
function mapEmployee(raw) {
    // B-34: l'API /api/employees ne retourne que display_name (pas given_name/surname).
    // On split display_name en fallback: "NOM Prénom" → surname = premier mot, givenName = reste.
    const displayNameRaw = str(raw.display_name || raw.displayName);
    const givenNameRaw = str(raw.given_name || raw.givenName);
    const surnameRaw = str(raw.surname);
    let resolvedGivenName = givenNameRaw;
    let resolvedSurname = surnameRaw;
    if (!resolvedGivenName && !resolvedSurname && displayNameRaw) {
        // Format attendu: "NOM Prénom" (majuscules pour nom de famille)
        const parts = displayNameRaw.trim().split(/\s+/);
        if (parts.length >= 2) {
            // Heuristique: si le premier mot est en majuscules, c'est le nom; sinon on prend le dernier
            const firstAllCaps = parts[0] === parts[0].toUpperCase() && parts[0].length > 1;
            if (firstAllCaps) {
                resolvedSurname = parts[0];
                resolvedGivenName = parts.slice(1).join(" ");
            }
            else {
                resolvedSurname = parts[parts.length - 1];
                resolvedGivenName = parts.slice(0, -1).join(" ");
            }
        }
        else {
            resolvedGivenName = displayNameRaw;
        }
    }
    return {
        id: str(raw.id),
        displayName: displayNameRaw,
        givenName: resolvedGivenName,
        surname: resolvedSurname,
        mail: str(raw.mail || raw.email),
        jobTitle: str(raw.job_title || raw.jobTitle),
        department: str(raw.department || raw.extension_attribute3),
        officeLocation: str(raw.office_location || raw.officeLocation || raw.extension_attribute2),
        mobilePhone: str(raw.mobile_phone || raw.mobilePhone),
        businessPhones: (raw.business_phones || raw.businessPhones || []),
        employeeId: str(raw.employee_id || raw.employeeId),
        userPrincipalName: str(raw.user_principal_name || raw.userPrincipalName || raw.email),
        accountEnabled: bool(raw.account_enabled ?? raw.accountEnabled, true),
        extensionAttribute1: str(raw.extension_attribute1 || raw.extensionAttribute1),
        extensionAttribute2: str(raw.extension_attribute2 || raw.extensionAttribute2),
        extensionAttribute3: str(raw.extension_attribute3 || raw.extensionAttribute3 || raw.service),
        extensionAttribute4: str(raw.extension_attribute4 || raw.extensionAttribute4),
        managerDisplayName: str(raw.manager_display_name || raw.managerDisplayName),
        managerMail: str(raw.manager_mail || raw.managerMail),
        onPremisesSyncEnabled: bool(raw.on_premises_sync_enabled ?? raw.onPremisesSyncEnabled),
        contratId: str(raw.contrat_id) || undefined,
        contratCode: str(raw.contrat_code) || undefined,
        contratLibelle: str(raw.contrat_libelle) || undefined,
        hasContrat: raw.has_contrat === true || !!raw.contrat_id,
    };
}
function mapContrat(raw) {
    return {
        id: str(raw.id),
        code: str(raw.code),
        libelle: str(raw.libelle),
        heuresSemaine: num(raw.heures_semaine),
        joursTravailSemaine: num(raw.jours_travail_semaine, 5),
        heuresJourDefaut: num(raw.heures_jour_defaut),
        joursCpAnnuel: num(raw.jours_cp_annuel, 25),
        joursRttAnnuel: num(raw.jours_rtt_annuel),
        description: str(raw.description),
        actif: bool(raw.actif, true),
    };
}
export async function getAllContrats(apiToken) {
    const data = await vpsGet("/api/contrats", apiToken);
    return data.map(mapContrat);
}
export async function createContrat(apiToken, fields) {
    const body = {
        code: fields.code, libelle: fields.libelle,
        heures_semaine: fields.heuresSemaine, jours_travail_semaine: fields.joursTravailSemaine,
        heures_jour_defaut: fields.heuresJourDefaut, jours_cp_annuel: fields.joursCpAnnuel,
        jours_rtt_annuel: fields.joursRttAnnuel, description: fields.description,
    };
    const res = await vpsPost("/api/contrats", body, apiToken);
    return str(res.id);
}
export async function updateContrat(apiToken, id, fields) {
    const body = {};
    if (fields.libelle !== undefined)
        body.libelle = fields.libelle;
    if (fields.heuresSemaine !== undefined)
        body.heures_semaine = fields.heuresSemaine;
    if (fields.joursTravailSemaine !== undefined)
        body.jours_travail_semaine = fields.joursTravailSemaine;
    if (fields.heuresJourDefaut !== undefined)
        body.heures_jour_defaut = fields.heuresJourDefaut;
    if (fields.joursCpAnnuel !== undefined)
        body.jours_cp_annuel = fields.joursCpAnnuel;
    if (fields.joursRttAnnuel !== undefined)
        body.jours_rtt_annuel = fields.joursRttAnnuel;
    if (fields.description !== undefined)
        body.description = fields.description;
    if (fields.actif !== undefined)
        body.actif = fields.actif;
    await vpsPatch(`/api/contrats/${id}`, body, apiToken);
}
export async function assignContratToEmployee(apiToken, employeeId, contratId) {
    await vpsPatch(`/api/contrats/employees/${employeeId}/contrat`, { contrat_id: contratId }, apiToken);
}
/** Récupère la liste des salariés depuis le VPS (DB locale synchronisée avec AD) */
export async function getSalaries(apiToken) {
    const data = await vpsGet("/api/employees", apiToken);
    // VPS DB ne stocke pas extensionAttribute1 — tous les employés actifs sont valides
    return data.filter((u) => u.email || u.mail).map(mapEmployee);
}
/** Récupère tous les employés depuis le VPS */
export async function getAllEmployees(apiToken) {
    const data = await vpsGet("/api/employees", apiToken);
    return data.map(mapEmployee);
}
/** Version légère — même endpoint, sans enrichissement managers */
export async function getAllEmployeesLight(apiToken) {
    const data = await vpsGet("/api/employees", apiToken);
    return data.map(mapEmployee);
}
/** Vérifie si extensionAttribute1 correspond à "Salarié" (insensible casse/accents) */
export function isSalarie(attr) {
    if (!attr)
        return false;
    const normalized = attr.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    return normalized === "salarie" || normalized === "salarié";
}
// ============ MODIFICATIONS GRAPH — restent sur graphClient ============
/** Modifie un extensionAttribute directement via Graph (fonctionne pour AD sync ET cloud-only) */
export async function patchUserExtensionAttribute(graphToken, userId, attrName, value) {
    await graphPatch(`/users/${userId}`, {
        onPremisesExtensionAttributes: { [attrName]: value },
    }, graphToken);
}
/** Modifie un extensionAttribute en masse — UNIQUEMENT les comptes cloud-only via Graph.
 *  Retourne aussi le nombre de comptes AD sync qui doivent être traités par le script AVHDC2. */
export async function bulkPatchExtensionAttribute(graphToken, attrName, oldValue, newValue) {
    const allUsers = [];
    let nextLink = `/users?$select=id,displayName,onPremisesSyncEnabled,onPremisesExtensionAttributes&$top=999&$filter=accountEnabled eq true`;
    while (nextLink) {
        const url = nextLink.startsWith("http") ? nextLink.replace("https://graph.microsoft.com/beta", "") : nextLink;
        const res = await graphGet(url, graphToken);
        allUsers.push(...res.value);
        nextLink = res["@odata.nextLink"] || null;
    }
    const matching = allUsers.filter((u) => {
        const val = u.onPremisesExtensionAttributes?.[attrName] || "";
        return oldValue ? val === oldValue : !val;
    });
    const cloudOnly = matching.filter((u) => !u.onPremisesSyncEnabled);
    const adSync = matching.filter((u) => u.onPremisesSyncEnabled);
    let cloudSuccess = 0;
    let cloudFailed = 0;
    for (let i = 0; i < cloudOnly.length; i += 10) {
        const batch = cloudOnly.slice(i, i + 10);
        const results = await Promise.allSettled(batch.map((u) => graphPatch(`/users/${u.id}`, {
            onPremisesExtensionAttributes: { [attrName]: newValue },
        }, graphToken)));
        for (const r of results) {
            if (r.status === "fulfilled")
                cloudSuccess++;
            else
                cloudFailed++;
        }
    }
    return { cloudSuccess, cloudFailed, cloudTotal: cloudOnly.length, adSyncCount: adSync.length };
}
/** Modifie le manager d'un user via Graph */
export async function setUserManager(graphToken, userId, managerUserId) {
    await graphPatch(`/users/${userId}/manager/$ref`, {
        "@odata.id": `https://graph.microsoft.com/beta/users/${managerUserId}`,
    }, graphToken);
}
// ============ EMAIL (Graph sendMail) ============
export async function sendChangeRequestEmail(graphToken, toEmail, subject, htmlBody) {
    await graphPost("/me/sendMail", {
        message: {
            subject,
            body: { contentType: "HTML", content: htmlBody },
            toRecipients: [{ emailAddress: { address: toEmail } }],
        },
        saveToSentItems: true,
    }, graphToken);
}
// ============ FICHIERS (UPLOAD SharePoint) ============
/** Upload un fichier dans la bibliothèque Documents du site SharePoint DRH.
 *  Stocke dans /DossiersSalaries/{employeeEmail}/{filename}.
 *  Retourne l'URL web du fichier. */
export async function uploadDossierFile(graphToken, employeeEmail, file) {
    const safeEmail = employeeEmail.replace(/[^a-zA-Z0-9@._-]/g, "_");
    const safeName = file.name.replace(/[#%&{}\\<>*?/$!'":@+`|=]/g, "_");
    const buffer = await file.arrayBuffer();
    const url = `https://graph.microsoft.com/v1.0/sites/${SHAREPOINT_SITE_ID}/drive/root:/DossiersSalaries/${safeEmail}/${safeName}:/content`;
    const response = await fetch(url, {
        method: "PUT",
        headers: {
            Authorization: `Bearer ${graphToken}`,
            "Content-Type": file.type || "application/octet-stream",
        },
        body: buffer,
    });
    if (!response.ok) {
        let detail = `${response.status} ${response.statusText}`;
        try {
            const body = await response.json();
            detail = body?.error?.message || detail;
        }
        catch { /* */ }
        throw new Error(`Upload échoué : ${detail}`);
    }
    const res = await response.json();
    return res.webUrl || "";
}
// ============ ALERTES BADGEAGE → /api/alertes-badgeage ============
function mapAlerteBadgeage(raw) {
    return {
        id: str(raw.id),
        employeeEmail: str(raw.employee_email),
        employeeName: str(raw.employee_name),
        dateAlerte: str(raw.date_alerte),
        typeAlerte: str(raw.type_alerte),
        message: str(raw.message),
        gravite: (str(raw.gravite) || "Info"),
        statut: (str(raw.statut) || "Ouverte"),
        emailEnvoye: bool(raw.email_envoye),
        createdAt: str(raw.created_at) || undefined,
    };
}
export async function getAlertesBadgeage(apiToken, statut) {
    const params = statut ? `?statut=${encodeURIComponent(statut)}` : "";
    const data = await vpsGet(`/api/alertes-badgeage${params}`, apiToken);
    return data.map(mapAlerteBadgeage);
}
export async function updateAlerteBadgeage(apiToken, itemId, fields) {
    // Convert camelCase to snake_case for PATCH body
    const body = {};
    if (fields.statut !== undefined)
        body.statut = fields.statut;
    if (fields.emailEnvoye !== undefined)
        body.email_envoye = fields.emailEnvoye;
    await vpsPatch(`/api/alertes-badgeage/${itemId}`, body, apiToken);
}
// ============ ALERTES BADGEAGE CONFIG → /api/alertes-badgeage-config ============
function mapAlerteBadgeageConfig(raw) {
    return {
        id: str(raw.id),
        userEmail: str(raw.user_email),
        userName: str(raw.user_name),
        typeConfig: (str(raw.type_config) || "Pilote"),
        ajoutePar: str(raw.ajoute_par),
        commentaire: str(raw.commentaire),
    };
}
export async function getAlertesBadgeageConfig(apiToken, type) {
    const params = type ? `?type=${encodeURIComponent(type)}` : "";
    const data = await vpsGet(`/api/alertes-badgeage-config${params}`, apiToken);
    return data.map(mapAlerteBadgeageConfig);
}
export async function addAlerteBadgeageConfig(apiToken, data) {
    return vpsPost(`/api/alertes-badgeage-config`, {
        user_email: data.userEmail,
        user_name: data.userName,
        type_config: data.typeConfig,
        commentaire: data.commentaire,
    }, apiToken);
}
export async function deleteAlerteBadgeageConfig(apiToken, itemId) {
    await vpsDelete(`/api/alertes-badgeage-config/${itemId}`, apiToken);
}
// ============ BADGE ENROLLEMENT → /api/badge-enrollement ============
function mapBadgeEnrollement(raw) {
    return {
        id: str(raw.id),
        employeeEmail: str(raw.employee_email),
        employeeName: str(raw.employee_name),
        employeeEntraId: str(raw.employee_entra_id),
        badgeUid: str(raw.badge_uid),
        serviceCible: str(raw.service_cible),
        horaireCible: str(raw.horaire_cible),
        profilBadgeage: str(raw.profil_badgeage),
        managerEmail: str(raw.manager_email),
        statut: (str(raw.statut) || "En attente"),
        erreurDetail: str(raw.erreur_detail),
        demandePar: str(raw.demande_par),
        dateTraitement: str(raw.date_traitement),
        kelioPersonneId: str(raw.kelio_personne_id),
        createdAt: str(raw.created_at) || undefined,
    };
}
export async function getBadgeEnrollements(apiToken, statut) {
    const params = statut ? `?statut=${encodeURIComponent(statut)}` : "";
    const data = await vpsGet(`/api/badge-enrollement${params}`, apiToken);
    return data.map(mapBadgeEnrollement);
}
export async function createBadgeEnrollement(apiToken, data) {
    return vpsPost(`/api/badge-enrollement`, {
        employee_email: data.employeeEmail,
        employee_name: data.employeeName,
        employee_entra_id: data.employeeEntraId,
        badge_uid: data.badgeUid,
        service_cible: data.serviceCible,
        horaire_cible: data.horaireCible,
        profil_badgeage: data.profilBadgeage,
        manager_email: data.managerEmail,
    }, apiToken);
}
// ============ DEMANDES CREATION COMPTE → /api/demandes-creation-compte ============
function mapDemandeCreationCompte(raw) {
    return {
        id: str(raw.id),
        codeUnique: str(raw.code_unique),
        prenom: str(raw.prenom),
        nom: str(raw.nom),
        upnPropose: str(raw.upn_propose),
        upnCree: str(raw.upn_cree),
        dateArrivee: str(raw.date_arrivee),
        categorie: str(raw.categorie),
        lieu: str(raw.lieu),
        service: str(raw.service),
        typeMachine: str(raw.type_machine),
        estResponsableService: bool(raw.est_responsable_service),
        managerEmail: str(raw.manager_email),
        managerName: str(raw.manager_name),
        mobile: str(raw.mobile),
        officeLocation: str(raw.office_location),
        jobTitle: str(raw.job_title),
        licenceO365: str(raw.licence_o365),
        telephonieOXE: str(raw.telephonie_oxe),
        motDePasseInitial: str(raw.mot_de_passe_initial),
        demandeurEmail: str(raw.demandeur_email),
        demandeurName: str(raw.demandeur_name),
        statut: (str(raw.statut) || "Brouillon RH"),
        messageErreur: str(raw.message_erreur),
        commentaireIT: str(raw.commentaire_it),
        approbateurEmail: str(raw.approbateur_email),
        dateApprobation: str(raw.date_approbation),
        dateTraitement: str(raw.date_traitement),
        entraUserId: str(raw.entra_user_id),
        createdAt: str(raw.created_at) || undefined,
    };
}
export async function getDemandesCreationCompte(apiToken, statut) {
    const params = statut ? `?statut=${encodeURIComponent(statut)}` : "";
    const data = await vpsGet(`/api/demandes-creation-compte${params}`, apiToken);
    return data.map(mapDemandeCreationCompte);
}
function toSnakeDemande(fields) {
    const map = {
        prenom: "prenom", nom: "nom",
        upnPropose: "upn_propose", upnCree: "upn_cree",
        dateArrivee: "date_arrivee", categorie: "categorie",
        lieu: "lieu", service: "service", typeMachine: "type_machine",
        estResponsableService: "est_responsable_service",
        managerEmail: "manager_email", managerName: "manager_name",
        mobile: "mobile", officeLocation: "office_location",
        jobTitle: "job_title", licenceO365: "licence_o365",
        telephonieOXE: "telephonie_oxe", motDePasseInitial: "mot_de_passe_initial",
        statut: "statut", messageErreur: "message_erreur",
        commentaireIT: "commentaire_it", entraUserId: "entra_user_id",
        action: "action",
    };
    const out = {};
    for (const [k, v] of Object.entries(fields)) {
        out[map[k] ?? k] = v;
    }
    return out;
}
export async function createDemandeCreationCompte(apiToken, data) {
    return vpsPost(`/api/demandes-creation-compte`, toSnakeDemande(data), apiToken);
}
export async function updateDemandeCreationCompte(apiToken, itemId, fields) {
    await vpsPatch(`/api/demandes-creation-compte/${itemId}`, toSnakeDemande(fields), apiToken);
}
export async function approveDemandeCreationCompte(apiToken, itemId, edits) {
    await vpsPatch(`/api/demandes-creation-compte/${itemId}`, { ...(edits ? toSnakeDemande(edits) : {}), action: "approve" }, apiToken);
}
export async function rejectDemandeCreationCompte(apiToken, itemId, commentaireIT) {
    await vpsPatch(`/api/demandes-creation-compte/${itemId}`, { action: "reject", commentaire_it: commentaireIT }, apiToken);
}
export async function deleteDemandeCreationCompte(apiToken, itemId) {
    await vpsDelete(`/api/demandes-creation-compte/${itemId}`, apiToken);
}
// ============ FORMATION → /api/formation ============
function mapFormationProgression(raw) {
    return {
        id: str(raw.id),
        userEmail: str(raw.user_email),
        tutorielId: str(raw.tutoriel_id),
        portailType: (str(raw.portail_type) || "Salarie"),
        statut: (str(raw.statut) || "En cours"),
        etapesCompletes: Array.isArray(raw.etapes_completes) ? raw.etapes_completes : [],
        derniereEtape: str(raw.derniere_etape),
        dateDebut: str(raw.date_debut),
        dateFin: str(raw.date_fin),
    };
}
export async function getFormationProgression(apiToken) {
    const data = await vpsGet(`/api/formation`, apiToken);
    return data.map(mapFormationProgression);
}
export async function saveFormationProgression(apiToken, data) {
    return vpsPost(`/api/formation`, {
        tutoriel_id: data.tutorielId,
        portail_type: data.portailType,
        etapes_completes: data.etapesCompletes,
        derniere_etape: data.derniereEtape,
        statut: data.statut,
    }, apiToken);
}
